import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { photoUrl } from "./api";
import { useContent } from "./content";
import { GOLD } from "./data";

const PHOTO_DURATION_MS = 5500;   // each photo shows for ~5.5s
const TRANSITION_MS = 900;        // crossfade duration

export default function HighlightReel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const { content } = useContent();
  const videoRef = useRef(null);
  const photoTimer = useRef(null);

  // Fetch reel media: items linked to any highlight
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/media", { params: { has_highlight: true, limit: 200 } });
        if (cancelled) return;
        // Shuffle once at mount so each visit feels fresh
        const shuffled = [...data].sort(() => Math.random() - 0.5);
        setItems(shuffled);
      } catch (e) {
        // Silent: reel just won't render
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pause when tab hidden
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const advance = useCallback(() => {
    setIdx((i) => (items.length ? (i + 1) % items.length : 0));
  }, [items.length]);

  // Photo timer: advance after PHOTO_DURATION_MS
  useEffect(() => {
    if (!items.length || paused) return;
    const current = items[idx];
    if (!current) return;
    const isVideo = current.type === "video" || (current.mime || "").startsWith("video/");
    if (isVideo) return; // video advances via its own onEnded handler
    photoTimer.current = setTimeout(advance, PHOTO_DURATION_MS);
    return () => clearTimeout(photoTimer.current);
  }, [idx, items, paused, advance]);

  // When video element changes, reset playback
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    if (paused) {
      v.pause();
    } else {
      const playPromise = v.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {});  // autoplay can fail silently on some browsers
      }
    }
  }, [idx, items, paused, muted]);

  if (!items.length) return null;

  const current = items[idx];
  const next = items[(idx + 1) % items.length];
  const isVideo = current.type === "video" || (current.mime || "").startsWith("video/");

  // Get linked highlight text for caption overlay
  const day = current.day_key
    ? content.days.find((d) => `d${d.day}` === current.day_key)
    : null;
  const highlight =
    day && current.highlight_index != null && day.highlights
      ? day.highlights[current.highlight_index]
      : null;

  return (
    <div className="jp-reel" data-testid="highlight-reel" aria-hidden>
      {/* Two layered slides for crossfade — current and next preloaded */}
      <div key={current.id} className="jp-reel-slide reel-current">
        {isVideo ? (
          <video
            ref={videoRef}
            src={photoUrl(current.url)}
            autoPlay
            muted={muted}
            playsInline
            onEnded={advance}
            data-testid="reel-video"
          />
        ) : (
          <img src={photoUrl(current.url)} alt="" />
        )}
      </div>

      {/* Preload next item with display:none so it's ready */}
      {next && next.id !== current.id && (
        <link
          rel="preload"
          as={(next.type === "video" || (next.mime || "").startsWith("video/")) ? "video" : "image"}
          href={photoUrl(next.url)}
        />
      )}

      {/* Darkening overlay + caption */}
      <div className="jp-reel-overlay" />
      {(highlight || current.caption) && (
        <div className="jp-reel-caption" data-testid="reel-caption">
          {day && (
            <div className="jp-reel-day">
              <span style={{ color: GOLD }}>★</span> Day {day.day} · {day.title || day.city}
            </div>
          )}
          {highlight && <div className="jp-reel-highlight">{highlight}</div>}
          {current.caption && <div className="jp-reel-cap-text">{current.caption}</div>}
        </div>
      )}

      <div className="jp-reel-controls">
        <button
          className="jp-reel-btn"
          onClick={() => setPaused((p) => !p)}
          title={paused ? "Play reel" : "Pause reel"}
          data-testid="reel-play-pause"
        >
          {paused ? "▶" : "❚❚"}
        </button>
        {isVideo && (
          <button
            className="jp-reel-btn"
            onClick={() => setMuted((m) => !m)}
            title={muted ? "Unmute" : "Mute"}
            data-testid="reel-mute"
          >
            {muted ? "🔇" : "🔊"}
          </button>
        )}
        <button
          className="jp-reel-btn"
          onClick={advance}
          title="Next"
          data-testid="reel-next"
        >
          ⏭
        </button>
      </div>

      <div className="jp-reel-progress" aria-hidden>
        <div className="jp-reel-dots">
          {items.slice(0, 12).map((it, i) => (
            <span key={it.id} className={`jp-reel-dot ${i === (idx % 12) ? "on" : ""}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
